import { z, type ZodType } from "zod";
import {
  dismissInboxNotificationResponseSchema,
  inboxNotificationCursorSchema,
  inboxNotificationIdSchema,
  inboxNotificationListResponseSchema,
  INBOX_NOTIFICATION_PAGE_LIMIT,
  markInboxNotificationReadResponseSchema,
  type DismissInboxNotificationResponse,
  type InboxNotificationListResponse,
  type MarkInboxNotificationReadResponse,
} from "./inbox-notification-schemas";
import {
  acceptJobOfferResponseSchema,
  createJobOfferInputSchema,
  createJobOfferResponseSchema,
  declineJobOfferResponseSchema,
  jobOfferActionInputSchema,
  jobOfferIdempotencyKeySchema,
  jobOfferIdSchema,
  jobOfferListResponseSchema,
  withdrawJobOfferResponseSchema,
  type AcceptJobOfferResponse,
  type CreateJobOfferInput,
  type CreateJobOfferResponse,
  type DeclineJobOfferResponse,
  type JobOfferActionInput,
  type JobOfferListResponse,
  type WithdrawJobOfferResponse,
} from "./job-offer-schemas";
import {
  preferredCleanerListResponseSchema,
  replacePreferredCleanersInputSchema,
  replacePreferredCleanersResponseSchema,
  type PreferredCleaner,
  type ReplacePreferredCleanersInput,
} from "./preferred-cleaner-schemas";
import {
  createPreferredCleanerSetInputSchema,
  createPreferredCleanerSetResponseSchema,
  deletePreferredCleanerSetResponseSchema,
  preferredCleanerSetIdSchema,
  preferredCleanerSetListResponseSchema,
  type CreatePreferredCleanerSetInput,
  type PreferredCleanerSet,
} from "./preferred-cleaner-set-schemas";
import {
  cleanerMarketplaceListResponseSchema,
  cleanerSearchResponseSchema,
  type CleanerDirectoryEntry,
  type CleanerMarketplaceEntry,
} from "./cleaner-directory-schemas";
import {
  cleanerInvitationChangedResponseSchema,
  cleanerInvitationDispatchResponseSchema,
  cleanerInvitationInboxResponseSchema,
  cleanerInvitationListResponseSchema,
  type CleanerInvitation,
  type CleanerInvitationInboxEntry,
  type CleanerInvitationStatus,
} from "./cleaner-invitation-schemas";
import {
  cleanerPlatformInvitationIdSchema,
  cleanerPlatformInvitationListResponseSchema,
  cleanerPlatformInvitationLookupResponseSchema,
  createCleanerPlatformInvitationInputSchema,
  createCleanerPlatformInvitationResponseSchema,
  revokeCleanerPlatformInvitationResponseSchema,
  type CleanerPlatformInvitation,
  type CreateCleanerPlatformInvitationInput,
  type ExistingCleanerLookup,
} from "./cleaner-platform-invitation-schemas";
import {
  propertyCalendarGetResponseSchema,
  propertyCalendarRevokeResponseSchema,
  propertyCalendarUpsertResponseSchema,
  type PropertyCalendar,
} from "./property-calendar-schemas";
import { checkoutRunListResponseSchema, type CheckoutRun } from "./checkout-run-schemas";
import {
  checkoutExceptionListResponseSchema,
  checkoutExceptionResolveResponseSchema,
  type CheckoutException,
} from "./checkout-exception-schemas";
import {
  linenOrderChangedResponseSchema,
  linenOrderListResponseSchema,
  type LinenOrder,
} from "./linen-order-schemas";
import {
  notificationPreferencesGetResponseSchema,
  notificationPreferencesUpsertResponseSchema,
  type NotificationPreferences,
} from "./notification-prefs-schemas";
import {
  applyForMarketJobResponseSchema,
  confirmJobApplicationInputSchema,
  confirmJobApplicationResponseSchema,
  currentProviderApplicationResponseSchema,
  customerApplicationsResponseSchema,
  jobApplicationIdSchema,
  type ApplyForMarketJobResponse,
  type ConfirmJobApplicationInput,
  type ConfirmJobApplicationResponse,
  type CustomerApplication,
  type ProviderApplication,
} from "./job-application-schemas";
import {
  cancelJobInputSchema,
  cancelJobResponseSchema,
  createJobInputSchema,
  createJobResponseSchema,
  jobIdSchema,
  jobListResponseSchema,
  jobResponseSchema,
  jobStatusSchema,
  publishJobInputSchema,
  publishJobResponseSchema,
  replaceJobServicesInputSchema,
  updateJobInputSchema,
  updateJobResponseSchema,
  type CancelJobInput,
  type CancelJobResponse,
  type CreateJobInput,
  type JobDetail,
  type JobStatus,
  type JobSummary,
  type PublishJobInput,
  type ReplaceJobServicesInput,
  type UpdateJobInput,
} from "./job-schemas";
import {
  acknowledgeJobCompletionInputSchema,
  acknowledgeJobCompletionResponseSchema,
  completionAcknowledgementIdempotencyKeySchema,
  jobCompletionAcknowledgementResponseSchema,
  type AcknowledgeJobCompletionInput,
  type AcknowledgeJobCompletionResponse,
  type JobCompletionAcknowledgement,
} from "./job-completion-ack-schemas";
import {
  createJobReplacementInputSchema,
  createJobReplacementResponseSchema,
  jobReplacementIdempotencyKeySchema,
  type CreateJobReplacementInput,
  type CreateJobReplacementResponse,
} from "./job-replacement-schemas";
import {
  actorPostServiceResponseSchema,
  disputeJobCompletionInputSchema,
  disputeJobCompletionResponseSchema,
  participantReviewInputSchema,
  participantReviewResponseSchema,
  postServiceIdempotencyKeySchema,
  providerBookingCompletionInputSchema,
  providerBookingCompletionResponseSchema,
  providerNoShowEligibilityResponseSchema,
  reportProviderNoShowInputSchema,
  reportProviderNoShowResponseSchema,
  submitParticipantReviewResponseSchema,
  type ActorPostService,
  type DisputeJobCompletionInput,
  type DisputeJobCompletionResponse,
  type ParticipantReview,
  type ParticipantReviewInput,
  type ProviderBookingCompletionInput,
  type ProviderBookingCompletionResponse,
  type ProviderNoShowEligibility,
  type ReportProviderNoShowInput,
  type ReportProviderNoShowResponse,
  type SubmitParticipantReviewResponse,
} from "./post-service-schemas";
import {
  marketJobIdSchema,
  marketJobListResponseSchema,
  marketJobResponseSchema,
  type MarketJobDetail,
  type MarketJobSummary,
} from "./market-job-schemas";
import {
  providerBookingActionInputSchema,
  providerBookingActionResponseSchema,
  providerBookingIdSchema,
  providerBookingListResponseSchema,
  providerBookingResponseSchema,
  type ProviderBookingActionInput,
  type ProviderBookingDetail,
  type ProviderBookingSummary,
} from "./provider-booking-schemas";
import {
  markMessageThreadReadInputSchema,
  markMessageThreadReadResponseSchema,
  messageCursorSchema,
  messageIdempotencyKeySchema,
  MESSAGE_THREAD_PAGE_LIMIT,
  messageThreadIdSchema,
  messageThreadListResponseSchema,
  sendThreadMessageInputSchema,
  sendThreadMessageResponseSchema,
  THREAD_MESSAGE_PAGE_LIMIT,
  threadMessageListResponseSchema,
  type MarkMessageThreadReadInput,
  type MarkMessageThreadReadResponse,
  type MessageThreadListResponse,
  type SendThreadMessageInput,
  type SendThreadMessageResponse,
  type ThreadMessageListResponse,
} from "./message-schemas";
import {
  catalogServiceListSchema,
  currentLegalDocumentsResponseSchema,
  currentOnboardingIntentsResponseSchema,
  currentUserResponseSchema,
  errorResponseSchema,
  linenItemTypeListSchema,
  legalAcceptanceInputSchema,
  legalAcceptanceResponseSchema,
  provisionCurrentUserResponseSchema,
  provisionProfileSchema,
  publicCurrentLegalDocumentsResponseSchema,
  replaceOnboardingIntentsInputSchema,
  updateCurrentUserPreferencesInputSchema,
  updateCurrentUserPreferencesResponseSchema,
  replaceOnboardingIntentsResponseSchema,
  serviceCategoryListSchema,
  supportedProfileLocaleSchema,
  type ApiValidationIssue,
  type CatalogServiceItem,
  type CurrentLegalDocumentsResponse,
  type CurrentUser,
  type OnboardingIntentState,
  type LegalAcceptanceInput,
  type LegalAcceptanceResponse,
  type LinenItemType,
  type ProvisionProfileInput,
  type PublicCurrentLegalDocumentsResponse,
  type ReplaceOnboardingIntentsInput,
  type UpdateCurrentUserPreferencesInput,
  type ReplaceOnboardingIntentsResponse,
  type ServiceCategory,
} from "./schemas";
import {
  activateLinenSubscriptionInputSchema,
  activateLinenSubscriptionResponseSchema,
  cancelLinenSubscriptionInputSchema,
  cancelLinenSubscriptionResponseSchema,
  createLinenConfigurationInputSchema,
  createLinenConfigurationResponseSchema,
  createLinenSubscriptionInputSchema,
  createLinenSubscriptionResponseSchema,
  linenPackageCatalogResponseSchema,
  linenSubscriptionIdSchema,
  linenSubscriptionListResponseSchema,
  linenSubscriptionResponseSchema,
  type ActivateLinenSubscriptionInput,
  type CancelLinenSubscriptionInput,
  type CreateLinenConfigurationInput,
  type CreateLinenSubscriptionInput,
  type LinenSubscriptionDetail,
  type LinenSubscriptionSummary,
  type PublishedLinenPackage,
} from "./linen-subscription-schemas";
import {
  createLinenServicePlanInputSchema,
  createLinenServicePlanResponseSchema,
  linenCycleIdSchema,
  linenCycleListResponseSchema,
  linenCycleResponseSchema,
  linenServicePlanIdSchema,
  linenServicePlanListResponseSchema,
  linenServicePlanResponseSchema,
  type CreateLinenServicePlanInput,
  type LinenCycle,
  type LinenServicePlan,
} from "./linen-service-plan-schemas";
import {
  completePrivateImageUploadResponseSchema,
  getProfileImageResponseSchema,
  privateImageMimeTypeSchema,
  propertyImageSetSchema,
  putProfileImageInputSchema,
  putProfileImageResponseSchema,
  putPropertyImageSetInputSchema,
  registerPrivateImageUploadInputSchema,
  registerPrivateImageUploadResponseSchema,
  type PrivateImageMedia,
  type PrivateImageUpload,
  type PropertyImageSet,
  type PutPropertyImageSetInput,
  type RegisterPrivateImageUploadInput,
} from "./media-schemas";
import {
  propertyAddressCatalogCitiesResponseSchema,
  propertyAddressCatalogCountiesResponseSchema,
  propertyAddressCatalogPostalCodesResponseSchema,
  propertyAddressCatalogStatesResponseSchema,
  type PropertyAddressCatalogCity,
  type PropertyAddressCatalogCounty,
  type PropertyAddressCatalogPostalCode,
  type PropertyAddressCatalogState,
} from "./property-address-catalog-schemas";
import {
  archivePropertyResponseSchema,
  createPropertyInputSchema,
  createPropertyResponseSchema,
  propertyAddressListResponseSchema,
  propertyBedListResponseSchema,
  propertyIdSchema,
  propertyListResponseSchema,
  propertyResponseSchema,
  replacePropertyAddressInputSchema,
  replacePropertyBedsInputSchema,
  updatePropertyInputSchema,
  type CreatePropertyInput,
  type PropertyAddress,
  type PropertyBed,
  type PropertyDetail,
  type PropertySummary,
  type ReplacePropertyAddressInput,
  type ReplacePropertyBedsInput,
  type UpdatePropertyInput,
} from "./property-schemas";
import {
  cleanerRoleResponseSchema,
  createProviderRateCardInputSchema,
  createProviderRateCardResponseSchema,
  createProviderResponseSchema,
  currentProviderApprovalResponseSchema,
  currentCleanerConfigurationResponseSchema,
  currentDryCleanerConfigurationResponseSchema,
  currentProviderRateCardsResponseSchema,
  currentProviderAvailabilityExceptionsResponseSchema,
  currentProviderServiceAreasResponseSchema,
  currentProviderServicesResponseSchema,
  currentProviderWeeklyAvailabilityResponseSchema,
  currentProviderResponseSchema,
  providerIdSchema,
  publishProviderRateCardInputSchema,
  publishProviderRateCardResponseSchema,
  replaceCleanerConfigurationInputSchema,
  replaceCleanerConfigurationResponseSchema,
  replaceDryCleanerConfigurationInputSchema,
  replaceDryCleanerConfigurationResponseSchema,
  replaceProviderAvailabilityExceptionsInputSchema,
  replaceProviderAvailabilityExceptionsResponseSchema,
  replaceProviderServiceAreasInputSchema,
  replaceProviderServiceAreasResponseSchema,
  replaceProviderServicesInputSchema,
  replaceProviderServicesResponseSchema,
  replaceProviderWeeklyAvailabilityInputSchema,
  replaceProviderWeeklyAvailabilityResponseSchema,
  submitProviderApprovalInputSchema,
  submitProviderApprovalResponseSchema,
  updateProviderBookingCapacityInputSchema,
  updateProviderBookingCapacityResponseSchema,
  type CleanerRoleResponse,
  type ProviderCleanerConfiguration,
  type ProviderCleanerConfigurationRead,
  type ReplaceCleanerConfigurationInput,
  type ProviderDryCleanerConfiguration,
  type ProviderDryCleanerConfigurationRead,
  type ReplaceDryCleanerConfigurationInput,
  type CreateProviderRateCardInput,
  type CreateProviderRateCardResponse,
  type IndependentProvider,
  type ProviderApproval,
  type ProviderAvailabilityException,
  type ProviderAvailabilityExceptionsConfiguration,
  type ProviderRateCardsRead,
  type ProviderServiceAreasConfiguration,
  type ProviderServiceAreasRead,
  type ProviderServicesConfiguration,
  type ProviderWeeklyAvailabilityConfiguration,
  type ProviderWeeklyAvailabilityWindow,
  type PublishProviderRateCardInput,
  type PublishProviderRateCardResponse,
  type ReplaceProviderAvailabilityExceptionsInput,
  type ReplaceProviderServiceAreasInput,
  type ReplaceProviderServicesInput,
  type ReplaceProviderWeeklyAvailabilityInput,
  type SubmitProviderApprovalInput,
  type SubmitProviderApprovalResponse,
  type UpdateProviderBookingCapacityInput,
} from "./provider-schemas";
import {
  createLaundryProviderFacilityInputSchema,
  createLaundryProviderFacilityResponseSchema,
  createLaundryProviderInputSchema,
  createLaundryProviderResponseSchema,
  currentLaundryProviderApprovalResponseSchema,
  currentLaundryProviderConfigurationResponseSchema,
  currentLaundryProviderResponseSchema,
  deleteLaundryProviderFacilityInputSchema,
  deleteLaundryProviderFacilityResponseSchema,
  laundryProviderActivationResponseSchema,
  laundryProviderFacilityListResponseSchema,
  replaceLaundryProviderConfigurationInputSchema,
  replaceLaundryProviderConfigurationResponseSchema,
  submitLaundryProviderApprovalInputSchema,
  submitLaundryProviderApprovalResponseSchema,
  updateLaundryProviderFacilityInputSchema,
  updateLaundryProviderFacilityResponseSchema,
  type CreateLaundryProviderFacilityInput,
  type CreateLaundryProviderInput,
  type DeleteLaundryProviderFacilityInput,
  type LaundryProvider,
  type LaundryProviderActivationResponse,
  type LaundryProviderApproval,
  type LaundryProviderConfiguration,
  type LaundryProviderFacility,
  type LaundryProviderFacilityWrite,
  type ReplaceLaundryProviderConfigurationInput,
  type SubmitLaundryProviderApprovalInput,
  type UpdateLaundryProviderFacilityInput,
} from "./laundry-provider-schemas";
import { validationIssueSummary } from "./error-presentation";

export type AccessTokenProvider = (forceRefresh: boolean) => Promise<string>;

const DEFAULT_REQUEST_TIMEOUT_MS = 15_000;
const MARKET_JOB_LIST_LIMIT = 25;
const CUSTOMER_APPLICATION_LIST_LIMIT = 50;
const ERROR_CONTRACT_ACCEPT = "application/vnd.washbox.error+json; version=2, application/json";

interface ApiErrorOptions extends ErrorOptions {
  readonly field?: string | null;
  readonly validationIssues?: readonly ApiValidationIssue[];
}

export class ApiError extends Error {
  public readonly field: string | null;
  public readonly validationIssues: readonly ApiValidationIssue[];

  public constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId: string | null = null,
    options?: ApiErrorOptions,
  ) {
    super(message, options);
    this.name = "ApiError";
    const validationIssues =
      options?.validationIssues !== undefined && options.validationIssues.length > 0
        ? options.validationIssues
        : options?.field === undefined || options.field === null
          ? []
          : [{ location: "body" as const, reason: "invalid_value", field: options.field }];
    this.validationIssues = Object.freeze([...validationIssues]);
    this.field =
      options?.field ??
      this.validationIssues.find(
        ({ location, field }) => location === "body" && field !== undefined,
      )?.field ??
      null;
  }
}

interface RequestOptions<T> {
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly authenticated?: boolean;
  readonly body?: unknown;
  readonly headers?: Readonly<Record<string, string>>;
  readonly schema: ZodType<T>;
}

function assertReplacementVersion(
  status: "updated" | "existing",
  requestedVersionNo: number,
  returnedVersionNo: number,
): void {
  const isValid =
    status === "updated"
      ? returnedVersionNo === requestedVersionNo + 1
      : returnedVersionNo >= requestedVersionNo;
  if (!isValid) {
    throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
  }
}

function assertExactReplacement(requested: readonly string[], returned: readonly string[]): void {
  const requestedSet = new Set(requested);
  const returnedSet = new Set(returned);
  if (
    requestedSet.size !== requested.length ||
    returnedSet.size !== returned.length ||
    requestedSet.size !== returnedSet.size ||
    [...requestedSet].some((value) => !returnedSet.has(value))
  ) {
    throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
  }
}

function weeklyWindowKey(window: ProviderWeeklyAvailabilityWindow): string {
  return `${window.dayOfWeek}|${window.startTime}|${window.endTime}`;
}

function availabilityExceptionKey(exception: ProviderAvailabilityException): string {
  return [
    exception.localDate,
    exception.mode,
    ...exception.windows
      .map(({ startTime, endTime }) => `${startTime}-${endTime}`)
      .sort((left, right) => left.localeCompare(right)),
  ].join("|");
}

export class WashboxApiClient {
  private readonly fetchImplementation: typeof fetch;

  public constructor(
    private readonly baseUrl: string,
    private readonly provideAccessToken: AccessTokenProvider,
    fetchImplementation: typeof fetch = fetch,
    private readonly requestTimeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS,
  ) {
    // Native browser fetch is a Window method. Preserve its global receiver
    // instead of invoking it with this API client as `this`.
    this.fetchImplementation = fetchImplementation.bind(globalThis);
  }

  private async request<T>(
    path: string,
    options: RequestOptions<T>,
    authenticationRetry = false,
  ): Promise<T> {
    const authenticated = options.authenticated ?? false;
    let response: Response;
    let payload: unknown;
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const timeout = new Promise<never>((_resolve, reject) => {
        timeoutId = setTimeout(() => {
          const timeoutError = new Error("Washbox API request timed out");
          controller.abort(timeoutError);
          reject(timeoutError);
        }, this.requestTimeoutMs);
      });

      ({ response, payload } = await Promise.race([
        (async () => {
          const headers = new Headers({ accept: ERROR_CONTRACT_ACCEPT });
          for (const [name, value] of Object.entries(options.headers ?? {})) {
            headers.set(name, value);
          }
          if (authenticated) {
            const token = await this.provideAccessToken(authenticationRetry);
            headers.set("authorization", `Bearer ${token}`);
          }
          if (options.body !== undefined) {
            headers.set("content-type", "application/json");
          }

          controller.signal.throwIfAborted();
          const fetchedResponse = await this.fetchImplementation(`${this.baseUrl}${path}`, {
            method: options.method ?? "GET",
            headers,
            signal: controller.signal,
            ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
          });
          return {
            response: fetchedResponse,
            payload: await this.readJson(fetchedResponse),
          };
        })(),
        timeout,
      ]));
    } catch (cause) {
      if (cause instanceof ApiError) {
        throw cause;
      }
      throw new ApiError(0, "NETWORK_ERROR", "Washbox could not be reached", null, { cause });
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    }

    if (!response.ok) {
      if (authenticated && response.status === 401 && !authenticationRetry) {
        return this.request(path, options, true);
      }

      const parsedError = errorResponseSchema.safeParse(payload);
      if (parsedError.success) {
        throw new ApiError(
          response.status,
          parsedError.data.error.code,
          parsedError.data.error.message,
          parsedError.data.error.requestId,
          {
            field: parsedError.data.error.field ?? null,
            validationIssues: parsedError.data.error.issues ?? [],
          },
        );
      }
      throw new ApiError(response.status, `HTTP_${response.status}`, "The request failed");
    }

    const parsed = options.schema.safeParse(payload);
    if (!parsed.success) {
      throw new ApiError(
        response.status,
        "INVALID_API_RESPONSE",
        "Washbox returned an unexpected response",
      );
    }
    return parsed.data;
  }

  private async requestImageBlob(path: string, authenticationRetry = false): Promise<Blob> {
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let response: Response;
    try {
      const timeout = new Promise<never>((_resolve, reject) => {
        timeoutId = setTimeout(() => {
          const timeoutError = new Error("Washbox API request timed out");
          controller.abort(timeoutError);
          reject(timeoutError);
        }, this.requestTimeoutMs);
      });
      response = await Promise.race([
        (async () => {
          const token = await this.provideAccessToken(authenticationRetry);
          controller.signal.throwIfAborted();
          return this.fetchImplementation(`${this.baseUrl}${path}`, {
            method: "GET",
            headers: {
              accept:
                "image/jpeg, image/png, image/webp, application/vnd.washbox.error+json; version=2, application/json",
              authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
            cache: "no-store",
          });
        })(),
        timeout,
      ]);
    } catch (cause) {
      if (cause instanceof ApiError) throw cause;
      throw new ApiError(0, "NETWORK_ERROR", "Washbox could not be reached", null, { cause });
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    }

    if (!response.ok) {
      if (response.status === 401 && !authenticationRetry) {
        return this.requestImageBlob(path, true);
      }
      const payload = await this.readJson(response);
      const parsedError = errorResponseSchema.safeParse(payload);
      if (parsedError.success) {
        throw new ApiError(
          response.status,
          parsedError.data.error.code,
          parsedError.data.error.message,
          parsedError.data.error.requestId,
          {
            field: parsedError.data.error.field ?? null,
            validationIssues: parsedError.data.error.issues ?? [],
          },
        );
      }
      throw new ApiError(response.status, `HTTP_${response.status}`, "The request failed");
    }

    const contentType = response.headers
      .get("content-type")
      ?.split(";", 1)[0]
      ?.trim()
      .toLowerCase();
    if (!privateImageMimeTypeSchema.safeParse(contentType).success) {
      throw new ApiError(
        response.status,
        "INVALID_API_RESPONSE",
        "Washbox returned an unexpected response",
      );
    }
    return response.blob();
  }

  private async readJson(response: Response): Promise<unknown> {
    const text = await response.text();
    if (text.length === 0) {
      return null;
    }
    try {
      return JSON.parse(text) as unknown;
    } catch {
      throw new ApiError(response.status, "INVALID_API_RESPONSE", "Washbox returned invalid JSON");
    }
  }

  public async listServiceCategories(): Promise<ServiceCategory[]> {
    return (
      await this.request("/catalog/service-categories", { schema: serviceCategoryListSchema })
    ).data;
  }

  public async listServices(categoryCode?: string): Promise<CatalogServiceItem[]> {
    const query =
      categoryCode === undefined ? "" : `?${new URLSearchParams({ categoryCode }).toString()}`;
    return (await this.request(`/catalog/services${query}`, { schema: catalogServiceListSchema }))
      .data;
  }

  public async listLinenItemTypes(): Promise<LinenItemType[]> {
    return (await this.request("/catalog/linen-item-types", { schema: linenItemTypeListSchema }))
      .data;
  }

  public async getCurrentUser(): Promise<CurrentUser> {
    return (
      await this.request("/me", {
        authenticated: true,
        schema: currentUserResponseSchema,
      })
    ).user;
  }

  public async provisionCurrentUser(input: ProvisionProfileInput): Promise<CurrentUser> {
    const body = provisionProfileSchema.parse(input);
    return (
      await this.request("/me", {
        method: "PUT",
        authenticated: true,
        body,
        schema: provisionCurrentUserResponseSchema,
      })
    ).user;
  }

  public async updateCurrentUserPreferences(
    input: UpdateCurrentUserPreferencesInput,
  ): Promise<CurrentUser> {
    const body = updateCurrentUserPreferencesInputSchema.parse(input);
    return (
      await this.request("/me/preferences", {
        method: "PATCH",
        authenticated: true,
        body,
        schema: updateCurrentUserPreferencesResponseSchema,
      })
    ).user;
  }

  public async getOnboardingIntents(): Promise<OnboardingIntentState> {
    return (
      await this.request("/me/onboarding-intents", {
        authenticated: true,
        schema: currentOnboardingIntentsResponseSchema,
      })
    ).onboarding;
  }

  public replaceOnboardingIntents(
    input: ReplaceOnboardingIntentsInput,
  ): Promise<ReplaceOnboardingIntentsResponse> {
    const body = replaceOnboardingIntentsInputSchema.parse(input);
    return this.request("/me/onboarding-intents", {
      method: "PUT",
      authenticated: true,
      body,
      schema: replaceOnboardingIntentsResponseSchema,
    });
  }

  public async registerImageUpload(
    input: RegisterPrivateImageUploadInput,
  ): Promise<PrivateImageUpload> {
    const body = registerPrivateImageUploadInputSchema.parse(input);
    return (
      await this.request("/media/uploads", {
        method: "POST",
        authenticated: true,
        body,
        schema: registerPrivateImageUploadResponseSchema,
      })
    ).upload;
  }

  public async completeImageUpload(mediaId: string): Promise<PrivateImageMedia> {
    const id = propertyIdSchema.parse(mediaId);
    const response = await this.request(`/media/${id}/actions/complete`, {
      method: "POST",
      authenticated: true,
      body: {},
      schema: completePrivateImageUploadResponseSchema,
    });
    if (response.media.mediaId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.media;
  }

  public downloadImageBlob(mediaId: string): Promise<Blob> {
    const id = propertyIdSchema.parse(mediaId);
    return this.requestImageBlob(`/media/${id}/download`);
  }

  public async getProfileImage(): Promise<PrivateImageMedia | null> {
    return (
      await this.request("/me/profile-image", {
        authenticated: true,
        schema: getProfileImageResponseSchema,
      })
    ).media;
  }

  public async putProfileImage(mediaId: string): Promise<PrivateImageMedia> {
    const body = putProfileImageInputSchema.parse({ mediaId });
    return (
      await this.request("/me/profile-image", {
        method: "PUT",
        authenticated: true,
        body,
        schema: putProfileImageResponseSchema,
      })
    ).media;
  }

  public async deleteProfileImage(): Promise<void> {
    await this.request("/me/profile-image", {
      method: "DELETE",
      authenticated: true,
      body: {},
      schema: z.null(),
    });
  }

  public async getPropertyImageSet(propertyId: string): Promise<PropertyImageSet> {
    const id = propertyIdSchema.parse(propertyId);
    return this.request(`/properties/${id}/images`, {
      authenticated: true,
      schema: propertyImageSetSchema,
    });
  }

  public async putPropertyImageSet(
    propertyId: string,
    input: PutPropertyImageSetInput,
  ): Promise<PropertyImageSet> {
    const id = propertyIdSchema.parse(propertyId);
    const body = putPropertyImageSetInputSchema.parse(input);
    const imageSet = await this.request(`/properties/${id}/images`, {
      method: "PUT",
      authenticated: true,
      body,
      schema: propertyImageSetSchema,
    });
    if (
      imageSet.versionNo < body.versionNo ||
      imageSet.media.length !== body.mediaIds.length ||
      imageSet.media.some((media, index) => media.mediaId !== body.mediaIds[index])
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return imageSet;
  }

  public requestCleanerRole(): Promise<CleanerRoleResponse> {
    return this.request("/me/roles/cleaner", {
      method: "POST",
      authenticated: true,
      schema: cleanerRoleResponseSchema,
    });
  }

  public activateLaundryProvider(): Promise<LaundryProviderActivationResponse> {
    return this.request("/laundry-providers/activation", {
      method: "POST",
      authenticated: true,
      schema: laundryProviderActivationResponseSchema,
    });
  }

  public async getCurrentLaundryProvider(): Promise<LaundryProvider> {
    return (
      await this.request("/laundry-providers/me", {
        authenticated: true,
        schema: currentLaundryProviderResponseSchema,
      })
    ).provider;
  }

  public async createLaundryProvider(input: CreateLaundryProviderInput): Promise<LaundryProvider> {
    const body = createLaundryProviderInputSchema.parse(input);
    return (
      await this.request("/laundry-providers", {
        method: "POST",
        authenticated: true,
        body,
        schema: createLaundryProviderResponseSchema,
      })
    ).provider;
  }

  public async getLaundryProviderConfiguration(): Promise<LaundryProviderConfiguration | null> {
    return (
      await this.request("/laundry-providers/me/configuration", {
        authenticated: true,
        schema: currentLaundryProviderConfigurationResponseSchema,
      })
    ).configuration;
  }

  public async replaceLaundryProviderConfiguration(
    input: ReplaceLaundryProviderConfigurationInput,
  ): Promise<LaundryProviderConfiguration> {
    const body = replaceLaundryProviderConfigurationInputSchema.parse(input);
    const response = await this.request("/laundry-providers/me/configuration", {
      method: "PUT",
      authenticated: true,
      body,
      schema: replaceLaundryProviderConfigurationResponseSchema,
    });
    assertReplacementVersion(response.status, body.versionNo, response.configuration.versionNo);
    if (
      response.configuration.perPoundPriceMinor !== body.perPoundPriceMinor ||
      response.configuration.billingAddress.cityCode !== body.billingAddress.cityCode ||
      response.configuration.billingAddress.postalCode !== body.billingAddress.postalCode
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.configuration;
  }

  public async listLaundryProviderFacilities(): Promise<LaundryProviderFacility[]> {
    return (
      await this.request("/laundry-providers/me/facilities", {
        authenticated: true,
        schema: laundryProviderFacilityListResponseSchema,
      })
    ).data;
  }

  public async createLaundryProviderFacility(
    input: CreateLaundryProviderFacilityInput,
  ): Promise<LaundryProviderFacility> {
    const body = createLaundryProviderFacilityInputSchema.parse(input);
    const response = await this.request("/laundry-providers/me/facilities", {
      method: "POST",
      authenticated: true,
      body,
      schema: createLaundryProviderFacilityResponseSchema,
    });
    if (
      response.facility.name !== body.name ||
      response.facility.coverageCityCode !== body.coverageCityCode
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertExactReplacement(body.coveragePostalCodes, response.facility.coveragePostalCodes);
    return response.facility;
  }

  public async updateLaundryProviderFacility(
    facilityId: string,
    input: UpdateLaundryProviderFacilityInput,
  ): Promise<LaundryProviderFacilityWrite> {
    const id = providerIdSchema.parse(facilityId);
    const body = updateLaundryProviderFacilityInputSchema.parse(input);
    const response = await this.request(`/laundry-providers/me/facilities/${id}`, {
      method: "PUT",
      authenticated: true,
      body,
      schema: updateLaundryProviderFacilityResponseSchema,
    });
    if (
      response.facility.id !== id ||
      response.facility.name !== body.name ||
      response.facility.coverageCityCode !== body.coverageCityCode
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertExactReplacement(body.coveragePostalCodes, response.facility.coveragePostalCodes);
    assertReplacementVersion(response.status, body.versionNo, response.facility.versionNo);
    return response.facility;
  }

  public async deleteLaundryProviderFacility(
    facilityId: string,
    input: DeleteLaundryProviderFacilityInput,
  ): Promise<void> {
    const id = providerIdSchema.parse(facilityId);
    const body = deleteLaundryProviderFacilityInputSchema.parse(input);
    const response = await this.request(`/laundry-providers/me/facilities/${id}`, {
      method: "DELETE",
      authenticated: true,
      body,
      schema: deleteLaundryProviderFacilityResponseSchema,
    });
    if (response.facilityId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
  }

  public async getLaundryProviderApproval(): Promise<LaundryProviderApproval> {
    return (
      await this.request("/laundry-providers/me/approval", {
        authenticated: true,
        schema: currentLaundryProviderApprovalResponseSchema,
      })
    ).approval;
  }

  public async submitLaundryProviderApproval(
    input: SubmitLaundryProviderApprovalInput,
  ): Promise<LaundryProviderApproval> {
    const body = submitLaundryProviderApprovalInputSchema.parse(input);
    const response = await this.request("/laundry-providers/me/approval/actions/submit", {
      method: "POST",
      authenticated: true,
      body,
      schema: submitLaundryProviderApprovalResponseSchema,
    });
    if (response.approval.providerVersionNo < body.providerVersionNo) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.approval;
  }

  public async getCurrentProvider(): Promise<IndependentProvider> {
    return (
      await this.request("/providers/me", {
        authenticated: true,
        schema: currentProviderResponseSchema,
      })
    ).provider;
  }

  public async createIndependentProvider(): Promise<IndependentProvider> {
    return (
      await this.request("/providers", {
        method: "POST",
        authenticated: true,
        schema: createProviderResponseSchema,
      })
    ).provider;
  }

  public async updateProviderBookingCapacity(
    providerId: string,
    input: UpdateProviderBookingCapacityInput,
  ): Promise<IndependentProvider> {
    const id = providerIdSchema.parse(providerId);
    const body = updateProviderBookingCapacityInputSchema.parse(input);
    const response = await this.request(`/providers/${id}/booking-capacity`, {
      method: "PUT",
      authenticated: true,
      body,
      schema: updateProviderBookingCapacityResponseSchema,
    });
    if (
      response.provider.id !== id ||
      response.provider.simultaneousBookingCapacity !== body.simultaneousBookingCapacity
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertReplacementVersion(response.status, body.versionNo, response.provider.versionNo);
    return response.provider;
  }

  public async getProviderApproval(providerId: string): Promise<ProviderApproval> {
    const id = providerIdSchema.parse(providerId);
    const response = await this.request(`/providers/${id}/approval`, {
      authenticated: true,
      schema: currentProviderApprovalResponseSchema,
    });
    if (response.approval.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.approval;
  }

  public async submitProviderApproval(
    providerId: string,
    input: SubmitProviderApprovalInput,
    idempotencyKey: string,
  ): Promise<SubmitProviderApprovalResponse> {
    const id = providerIdSchema.parse(providerId);
    const body = submitProviderApprovalInputSchema.parse(input);
    const response = await this.request(`/providers/${id}/approval/actions/submit`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": idempotencyKey },
      body,
      schema: submitProviderApprovalResponseSchema,
    });
    if (
      response.approval.providerId !== id ||
      response.approval.providerVersionNo < body.providerVersionNo
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async getCleanerConfiguration(
    providerId: string,
  ): Promise<ProviderCleanerConfigurationRead> {
    const id = providerIdSchema.parse(providerId);
    const response = await this.request(`/providers/${id}/cleaner-configuration`, {
      authenticated: true,
      schema: currentCleanerConfigurationResponseSchema,
    });
    if (response.configuration !== null && response.configuration.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async replaceCleanerConfiguration(
    providerId: string,
    input: ReplaceCleanerConfigurationInput,
  ): Promise<ProviderCleanerConfiguration> {
    const id = providerIdSchema.parse(providerId);
    const body = replaceCleanerConfigurationInputSchema.parse(input);
    const response = await this.request(`/providers/${id}/cleaner-configuration`, {
      method: "PUT",
      authenticated: true,
      body,
      schema: replaceCleanerConfigurationResponseSchema,
    });
    const configuration = response.configuration;
    const hasExactPrices = <Item extends { [key: string]: unknown }>(
      requested: readonly Item[],
      returned: readonly Item[],
      key: keyof Item,
    ): boolean =>
      requested.length === returned.length &&
      requested.every((item) => {
        const matched = returned.find((candidate) => candidate[key] === item[key]);
        return matched !== undefined && JSON.stringify(matched) === JSON.stringify(item);
      });
    if (
      configuration.providerId !== id ||
      configuration.billingAddress.streetLine1 !== body.billingAddress.streetLine1 ||
      configuration.billingAddress.streetLine2 !== body.billingAddress.streetLine2 ||
      configuration.billingAddress.cityCode !== body.billingAddress.cityCode ||
      configuration.billingAddress.postalCode !== body.billingAddress.postalCode ||
      configuration.workArea.cityCode !== body.workArea.cityCode ||
      !hasExactPrices(body.basePrices, configuration.basePrices, "cleaningType") ||
      !hasExactPrices(body.paidAddons, configuration.paidAddons, "addonCode")
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertExactReplacement(body.workArea.postalCodes, configuration.workArea.postalCodes);
    assertReplacementVersion(response.status, body.versionNo, configuration.versionNo);
    return configuration;
  }

  public async getDryCleanerConfiguration(
    providerId: string,
  ): Promise<ProviderDryCleanerConfigurationRead> {
    const id = providerIdSchema.parse(providerId);
    const response = await this.request(`/providers/${id}/dry-cleaner-configuration`, {
      authenticated: true,
      schema: currentDryCleanerConfigurationResponseSchema,
    });
    if (response.configuration !== null && response.configuration.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async replaceDryCleanerConfiguration(
    providerId: string,
    input: ReplaceDryCleanerConfigurationInput,
  ): Promise<ProviderDryCleanerConfiguration> {
    const id = providerIdSchema.parse(providerId);
    const body = replaceDryCleanerConfigurationInputSchema.parse(input);
    const response = await this.request(`/providers/${id}/dry-cleaner-configuration`, {
      method: "PUT",
      authenticated: true,
      body,
      schema: replaceDryCleanerConfigurationResponseSchema,
    });
    if (response.configuration.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertReplacementVersion(response.status, body.versionNo, response.configuration.versionNo);
    assertExactReplacement(body.deliveryPostalCodes, response.configuration.deliveryPostalCodes);
    return response.configuration;
  }

  public async getProviderServices(providerId: string): Promise<ProviderServicesConfiguration> {
    const id = providerIdSchema.parse(providerId);
    const response = await this.request(`/providers/${id}/services`, {
      authenticated: true,
      schema: currentProviderServicesResponseSchema,
    });
    if (response.configuration.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.configuration;
  }

  public async replaceProviderServices(
    providerId: string,
    input: ReplaceProviderServicesInput,
  ): Promise<ProviderServicesConfiguration> {
    const id = providerIdSchema.parse(providerId);
    const body = replaceProviderServicesInputSchema.parse(input);
    const response = await this.request(`/providers/${id}/services`, {
      method: "PUT",
      authenticated: true,
      body,
      schema: replaceProviderServicesResponseSchema,
    });
    if (response.configuration.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertReplacementVersion(response.status, body.versionNo, response.configuration.versionNo);
    return response.configuration;
  }

  public async getProviderRateCards(providerId: string): Promise<ProviderRateCardsRead> {
    const id = providerIdSchema.parse(providerId);
    const response = await this.request(`/providers/${id}/rate-cards`, {
      authenticated: true,
      schema: currentProviderRateCardsResponseSchema,
    });
    if (response.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async createProviderRateCard(
    providerId: string,
    input: CreateProviderRateCardInput,
    idempotencyKey: string,
  ): Promise<CreateProviderRateCardResponse> {
    const id = providerIdSchema.parse(providerId);
    const body = createProviderRateCardInputSchema.parse(input);
    const response = await this.request(`/providers/${id}/rate-cards`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": idempotencyKey },
      body,
      schema: createProviderRateCardResponseSchema,
    });
    if (
      response.rateCard.providerId !== id ||
      response.rateCard.status !== "DRAFT" ||
      response.providerVersionNo < body.providerVersionNo
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertExactReplacement(
      body.lines.map(({ serviceId, amountMinor }) => `${serviceId}|${amountMinor}`),
      response.rateCard.lines.map(({ serviceId, amountMinor }) => `${serviceId}|${amountMinor}`),
    );
    return response;
  }

  public async publishProviderRateCard(
    providerId: string,
    rateCardId: string,
    input: PublishProviderRateCardInput,
    idempotencyKey: string,
  ): Promise<PublishProviderRateCardResponse> {
    const id = providerIdSchema.parse(providerId);
    const cardId = providerIdSchema.parse(rateCardId);
    const body = publishProviderRateCardInputSchema.parse(input);
    const response = await this.request(`/providers/${id}/rate-cards/${cardId}/actions/publish`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": idempotencyKey },
      body,
      schema: publishProviderRateCardResponseSchema,
    });
    if (
      response.rateCard.id !== cardId ||
      response.rateCard.providerId !== id ||
      response.rateCard.status !== "PUBLISHED" ||
      response.providerVersionNo < body.providerVersionNo ||
      response.rateCard.versionNo !== body.rateCardVersionNo
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async getProviderServiceAreas(providerId: string): Promise<ProviderServiceAreasRead> {
    const id = providerIdSchema.parse(providerId);
    const response = await this.request(`/providers/${id}/service-areas`, {
      authenticated: true,
      schema: currentProviderServiceAreasResponseSchema,
    });
    if (response.configuration.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return {
      configuration: response.configuration,
      supportedPostalCodes: response.supportedPostalCodes,
    };
  }

  public async replaceProviderServiceAreas(
    providerId: string,
    input: ReplaceProviderServiceAreasInput,
  ): Promise<ProviderServiceAreasConfiguration> {
    const id = providerIdSchema.parse(providerId);
    const body = replaceProviderServiceAreasInputSchema.parse(input);
    const response = await this.request(`/providers/${id}/service-areas`, {
      method: "PUT",
      authenticated: true,
      body,
      schema: replaceProviderServiceAreasResponseSchema,
    });
    if (response.configuration.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertReplacementVersion(response.status, body.versionNo, response.configuration.versionNo);
    const returnedPostalCodes = new Set(response.configuration.postalCodes);
    if (
      returnedPostalCodes.size !== body.postalCodes.length ||
      body.postalCodes.some((postalCode) => !returnedPostalCodes.has(postalCode))
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.configuration;
  }

  public async getProviderWeeklyAvailability(
    providerId: string,
  ): Promise<ProviderWeeklyAvailabilityConfiguration> {
    const id = providerIdSchema.parse(providerId);
    const response = await this.request(`/providers/${id}/availability/weekly`, {
      authenticated: true,
      schema: currentProviderWeeklyAvailabilityResponseSchema,
    });
    if (response.configuration.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.configuration;
  }

  public async replaceProviderWeeklyAvailability(
    providerId: string,
    input: ReplaceProviderWeeklyAvailabilityInput,
  ): Promise<ProviderWeeklyAvailabilityConfiguration> {
    const id = providerIdSchema.parse(providerId);
    const body = replaceProviderWeeklyAvailabilityInputSchema.parse(input);
    const response = await this.request(`/providers/${id}/availability/weekly`, {
      method: "PUT",
      authenticated: true,
      body,
      schema: replaceProviderWeeklyAvailabilityResponseSchema,
    });
    if (response.configuration.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertReplacementVersion(response.status, body.versionNo, response.configuration.versionNo);
    assertExactReplacement(
      body.windows.map(weeklyWindowKey),
      response.configuration.windows.map(weeklyWindowKey),
    );
    return response.configuration;
  }

  public async getProviderAvailabilityExceptions(
    providerId: string,
  ): Promise<ProviderAvailabilityExceptionsConfiguration> {
    const id = providerIdSchema.parse(providerId);
    const response = await this.request(`/providers/${id}/availability/exceptions`, {
      authenticated: true,
      schema: currentProviderAvailabilityExceptionsResponseSchema,
    });
    if (response.configuration.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.configuration;
  }

  public async replaceProviderAvailabilityExceptions(
    providerId: string,
    input: ReplaceProviderAvailabilityExceptionsInput,
  ): Promise<ProviderAvailabilityExceptionsConfiguration> {
    const id = providerIdSchema.parse(providerId);
    const body = replaceProviderAvailabilityExceptionsInputSchema.parse(input);
    const response = await this.request(`/providers/${id}/availability/exceptions`, {
      method: "PUT",
      authenticated: true,
      body,
      schema: replaceProviderAvailabilityExceptionsResponseSchema,
    });
    if (response.configuration.providerId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertReplacementVersion(response.status, body.versionNo, response.configuration.versionNo);
    assertExactReplacement(
      body.exceptions.map(availabilityExceptionKey),
      response.configuration.exceptions.map(availabilityExceptionKey),
    );
    return response.configuration;
  }

  public async listInboxNotifications(cursor?: string): Promise<InboxNotificationListResponse> {
    const query = new URLSearchParams({ limit: String(INBOX_NOTIFICATION_PAGE_LIMIT) });
    if (cursor !== undefined) query.set("cursor", inboxNotificationCursorSchema.parse(cursor));
    return this.request(`/notifications?${query.toString()}`, {
      authenticated: true,
      schema: inboxNotificationListResponseSchema,
    });
  }

  public async markInboxNotificationRead(
    notificationId: string,
  ): Promise<MarkInboxNotificationReadResponse> {
    const id = inboxNotificationIdSchema.parse(notificationId);
    const response = await this.request(`/notifications/${id}/actions/read`, {
      method: "POST",
      authenticated: true,
      schema: markInboxNotificationReadResponseSchema,
    });
    const responseNotificationId =
      response.status === "unavailable" ? response.notificationId : response.notification.id;
    if (responseNotificationId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async dismissInboxNotification(
    notificationId: string,
  ): Promise<DismissInboxNotificationResponse> {
    const id = inboxNotificationIdSchema.parse(notificationId);
    const response = await this.request(`/notifications/${id}/actions/dismiss`, {
      method: "POST",
      authenticated: true,
      schema: dismissInboxNotificationResponseSchema,
    });
    if (response.notificationId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async listMessageThreads(
    options: { readonly cursor?: string; readonly jobId?: string } = {},
  ): Promise<MessageThreadListResponse> {
    const filters = z
      .strictObject({
        cursor: messageCursorSchema.optional(),
        jobId: jobIdSchema.optional(),
      })
      .parse(options);
    const query = new URLSearchParams({ limit: String(MESSAGE_THREAD_PAGE_LIMIT) });
    if (filters.cursor !== undefined) query.set("cursor", filters.cursor);
    const filteredJobId = filters.jobId;
    if (filteredJobId !== undefined) query.set("jobId", filteredJobId);

    const response = await this.request(`/threads?${query.toString()}`, {
      authenticated: true,
      schema: messageThreadListResponseSchema,
    });
    if (
      filteredJobId !== undefined &&
      response.threads.some(({ jobId: threadJobId }) => threadJobId !== filteredJobId)
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async listThreadMessages(
    threadId: string,
    cursor?: string,
  ): Promise<ThreadMessageListResponse> {
    const id = messageThreadIdSchema.parse(threadId);
    const query = new URLSearchParams({ limit: String(THREAD_MESSAGE_PAGE_LIMIT) });
    if (cursor !== undefined) query.set("cursor", messageCursorSchema.parse(cursor));
    const response = await this.request(`/threads/${id}/messages?${query.toString()}`, {
      authenticated: true,
      schema: threadMessageListResponseSchema,
    });
    if (response.thread.id !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async sendThreadMessage(
    threadId: string,
    input: SendThreadMessageInput,
    idempotencyKey: string,
  ): Promise<SendThreadMessageResponse> {
    const id = messageThreadIdSchema.parse(threadId);
    const body = sendThreadMessageInputSchema.parse(input);
    const key = messageIdempotencyKeySchema.parse(idempotencyKey);
    const response = await this.request(`/threads/${id}/messages`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": key },
      body,
      schema: sendThreadMessageResponseSchema,
    });
    if (
      response.message.threadId !== id ||
      response.message.bodyText !== body.bodyText ||
      response.message.replyToMessageId !== body.replyToMessageId
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async markMessageThreadRead(
    threadId: string,
    input: MarkMessageThreadReadInput,
  ): Promise<MarkMessageThreadReadResponse> {
    const id = messageThreadIdSchema.parse(threadId);
    const body = markMessageThreadReadInputSchema.parse(input);
    const response = await this.request(`/threads/${id}/actions/read`, {
      method: "POST",
      authenticated: true,
      body,
      schema: markMessageThreadReadResponseSchema,
    });
    if (response.threadId !== id || response.lastReadSequenceNo < body.throughSequenceNo) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async getPublicCurrentLegalDocuments(
    localeCode: "en-US" | "en-GB",
  ): Promise<PublicCurrentLegalDocumentsResponse> {
    const locale = supportedProfileLocaleSchema.parse(localeCode);
    const query = new URLSearchParams({ localeCode: locale }).toString();
    return this.request(`/legal/public/documents/current?${query}`, {
      schema: publicCurrentLegalDocumentsResponseSchema,
    });
  }

  public async getCurrentLegalDocuments(): Promise<CurrentLegalDocumentsResponse> {
    return this.request("/legal/documents/current", {
      authenticated: true,
      schema: currentLegalDocumentsResponseSchema,
    });
  }

  public async acceptLegalDocument(input: LegalAcceptanceInput): Promise<LegalAcceptanceResponse> {
    const body = legalAcceptanceInputSchema.parse(input);
    return this.request("/legal/acceptances", {
      method: "POST",
      authenticated: true,
      body,
      schema: legalAcceptanceResponseSchema,
    });
  }

  public async listBookings(): Promise<ProviderBookingSummary[]> {
    const response = await this.request("/bookings", {
      authenticated: true,
      schema: providerBookingListResponseSchema,
    });
    return response.bookings;
  }

  public async getBooking(jobId: string): Promise<ProviderBookingDetail> {
    const id = providerBookingIdSchema.parse(jobId);
    const response = await this.request(`/bookings/${id}`, {
      authenticated: true,
      schema: providerBookingResponseSchema,
    });
    if (response.booking.id !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.booking;
  }

  private async performBookingAction(
    action: "start" | "cancel",
    jobId: string,
    input: ProviderBookingActionInput,
    idempotencyKey: string,
  ): Promise<ProviderBookingDetail> {
    const id = providerBookingIdSchema.parse(jobId);
    const body = providerBookingActionInputSchema.parse(input);
    const response = await this.request(`/bookings/${id}/actions/${action}`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": idempotencyKey },
      body,
      schema: providerBookingActionResponseSchema,
    });
    if (response.booking.id !== id || response.booking.jobVersionNo < body.jobVersionNo) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    const expectedStatus = action === "start" ? "IN_PROGRESS" : "CANCELLED";
    if (response.booking.jobStatus !== expectedStatus) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.booking;
  }

  public startBooking(
    jobId: string,
    input: ProviderBookingActionInput,
    idempotencyKey: string,
  ): Promise<ProviderBookingDetail> {
    return this.performBookingAction("start", jobId, input, idempotencyKey);
  }

  public async completeBooking(
    jobId: string,
    input: ProviderBookingCompletionInput,
    idempotencyKey: string,
  ): Promise<ProviderBookingCompletionResponse> {
    const id = providerBookingIdSchema.parse(jobId);
    const body = providerBookingCompletionInputSchema.parse(input);
    const key = postServiceIdempotencyKeySchema.parse(idempotencyKey);
    const response = await this.request(`/bookings/${id}/actions/complete`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": key },
      body,
      schema: providerBookingCompletionResponseSchema,
    });
    if (
      response.booking.id !== id ||
      response.completion.jobId !== id ||
      response.completion.jobVersionNo !== body.jobVersionNo + 1 ||
      response.completion.completionNote !== body.completionNote
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertExactReplacement(body.completedServiceIds, response.completion.completedServiceIds);
    return response;
  }

  public cancelBooking(
    jobId: string,
    input: ProviderBookingActionInput,
    idempotencyKey: string,
  ): Promise<ProviderBookingDetail> {
    return this.performBookingAction("cancel", jobId, input, idempotencyKey);
  }

  public async getProviderPostService(jobId: string): Promise<ActorPostService> {
    const id = providerBookingIdSchema.parse(jobId);
    const response = await this.request(`/bookings/${id}/post-service`, {
      authenticated: true,
      schema: actorPostServiceResponseSchema,
    });
    if (response.postService.jobId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.postService;
  }

  public async getProviderReview(jobId: string): Promise<ParticipantReview> {
    const id = providerBookingIdSchema.parse(jobId);
    const response = await this.request(`/bookings/${id}/review`, {
      authenticated: true,
      schema: participantReviewResponseSchema,
    });
    if (response.review.reviewerSide !== "PROVIDER") {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.review;
  }

  public async submitProviderReview(
    jobId: string,
    input: ParticipantReviewInput,
    idempotencyKey: string,
  ): Promise<SubmitParticipantReviewResponse> {
    const id = providerBookingIdSchema.parse(jobId);
    const body = participantReviewInputSchema.parse(input);
    const key = postServiceIdempotencyKeySchema.parse(idempotencyKey);
    const response = await this.request(`/bookings/${id}/review`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": key },
      body,
      schema: submitParticipantReviewResponseSchema,
    });
    if (
      response.review.reviewerSide !== "PROVIDER" ||
      response.review.rating !== body.rating ||
      response.review.comment !== body.comment
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async listMarketJobs(): Promise<MarketJobSummary[]> {
    const response = await this.request(`/market/jobs?limit=${MARKET_JOB_LIST_LIMIT}`, {
      authenticated: true,
      schema: marketJobListResponseSchema,
    });
    if (response.jobs.length > MARKET_JOB_LIST_LIMIT) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.jobs;
  }

  public async getMarketJob(jobId: string): Promise<MarketJobDetail> {
    const id = marketJobIdSchema.parse(jobId);
    const response = await this.request(`/market/jobs/${id}`, {
      authenticated: true,
      schema: marketJobResponseSchema,
    });
    if (response.job.id !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.job;
  }

  public async applyForMarketJob(
    jobId: string,
    idempotencyKey: string,
  ): Promise<ApplyForMarketJobResponse> {
    const id = marketJobIdSchema.parse(jobId);
    const response = await this.request(`/market/jobs/${id}/applications`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": idempotencyKey },
      schema: applyForMarketJobResponseSchema,
    });
    if (response.application.jobId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async getMyMarketJobApplication(jobId: string): Promise<ProviderApplication | null> {
    const id = marketJobIdSchema.parse(jobId);
    const response = await this.request(`/market/jobs/${id}/applications/me`, {
      authenticated: true,
      schema: currentProviderApplicationResponseSchema,
    });
    if (response.application !== null && response.application.jobId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.application;
  }

  public async listJobApplications(jobId: string): Promise<CustomerApplication[]> {
    const id = jobIdSchema.parse(jobId);
    const response = await this.request(
      `/jobs/${id}/applications?limit=${CUSTOMER_APPLICATION_LIST_LIMIT}`,
      {
        authenticated: true,
        schema: customerApplicationsResponseSchema,
      },
    );
    if (response.applications.some(({ jobId: applicationJobId }) => applicationJobId !== id)) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.applications;
  }

  public async confirmJobApplication(
    jobId: string,
    applicationId: string,
    input: ConfirmJobApplicationInput,
    idempotencyKey: string,
  ): Promise<ConfirmJobApplicationResponse> {
    const id = jobIdSchema.parse(jobId);
    const selectedApplicationId = jobApplicationIdSchema.parse(applicationId);
    const body = confirmJobApplicationInputSchema.parse(input);
    const response = await this.request(
      `/jobs/${id}/applications/${selectedApplicationId}/actions/confirm`,
      {
        method: "POST",
        authenticated: true,
        headers: { "idempotency-key": idempotencyKey },
        body,
        schema: confirmJobApplicationResponseSchema,
      },
    );
    if (
      response.booking.jobId !== id ||
      response.booking.applicationId !== selectedApplicationId ||
      response.booking.jobVersionNo !== body.jobVersionNo + 1
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async listJobOffers(jobId: string): Promise<JobOfferListResponse> {
    const id = jobIdSchema.parse(jobId);
    const response = await this.request(`/jobs/${id}/offers`, {
      authenticated: true,
      schema: jobOfferListResponseSchema,
    });
    if (response.jobId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async createJobOffer(
    jobId: string,
    input: CreateJobOfferInput,
    idempotencyKey: string,
  ): Promise<CreateJobOfferResponse> {
    const id = jobIdSchema.parse(jobId);
    const body = createJobOfferInputSchema.parse(input);
    const key = jobOfferIdempotencyKeySchema.parse(idempotencyKey);
    const response = await this.request(`/jobs/${id}/offers`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": key },
      body,
      schema: createJobOfferResponseSchema,
    });
    const expectedRevisionNo = body.chainVersionNo === null ? 1 : body.chainVersionNo + 1;
    if (
      response.jobId !== id ||
      (body.chainId !== null && response.chainId !== body.chainId) ||
      response.revisionNo !== expectedRevisionNo ||
      response.chainVersionNo !== expectedRevisionNo
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  private async performJobOfferAction<
    T extends AcceptJobOfferResponse | DeclineJobOfferResponse | WithdrawJobOfferResponse,
  >(
    action: "accept" | "decline" | "withdraw",
    offerId: string,
    input: JobOfferActionInput,
    idempotencyKey: string,
    schema: ZodType<T>,
  ): Promise<T> {
    const id = jobOfferIdSchema.parse(offerId);
    const body = jobOfferActionInputSchema.parse(input);
    const key = jobOfferIdempotencyKeySchema.parse(idempotencyKey);
    const response = await this.request(`/offers/${id}/actions/${action}`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": key },
      body,
      schema,
    });
    if (
      response.offerId !== id ||
      response.jobId !== body.jobId ||
      response.chainId !== body.chainId
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async acceptJobOffer(
    offerId: string,
    input: JobOfferActionInput,
    idempotencyKey: string,
  ): Promise<AcceptJobOfferResponse> {
    const response = await this.performJobOfferAction(
      "accept",
      offerId,
      input,
      idempotencyKey,
      acceptJobOfferResponseSchema,
    );
    if (response.jobVersionNo !== input.jobVersionNo + 1) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public declineJobOffer(
    offerId: string,
    input: JobOfferActionInput,
    idempotencyKey: string,
  ): Promise<DeclineJobOfferResponse> {
    return this.performJobOfferAction(
      "decline",
      offerId,
      input,
      idempotencyKey,
      declineJobOfferResponseSchema,
    );
  }

  public withdrawJobOffer(
    offerId: string,
    input: JobOfferActionInput,
    idempotencyKey: string,
  ): Promise<WithdrawJobOfferResponse> {
    return this.performJobOfferAction(
      "withdraw",
      offerId,
      input,
      idempotencyKey,
      withdrawJobOfferResponseSchema,
    );
  }

  public async listJobs(
    options: { status?: JobStatus; limit?: number } = {},
  ): Promise<JobSummary[]> {
    const filters = z
      .strictObject({
        status: jobStatusSchema.optional(),
        limit: z.number().int().min(1).max(100).optional(),
      })
      .parse(options);
    const query = new URLSearchParams();
    if (filters.status !== undefined) query.set("status", filters.status);
    if (filters.limit !== undefined) query.set("limit", String(filters.limit));
    const suffix = query.size === 0 ? "" : `?${query.toString()}`;
    const response = await this.request(`/jobs${suffix}`, {
      authenticated: true,
      schema: jobListResponseSchema,
    });
    if (
      filters.status !== undefined &&
      response.jobs.some(({ jobStatus }) => jobStatus !== filters.status)
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.jobs;
  }

  public async getJob(jobId: string): Promise<JobDetail> {
    const id = jobIdSchema.parse(jobId);
    const response = await this.request(`/jobs/${id}`, {
      authenticated: true,
      schema: jobResponseSchema,
    });
    if (response.job.id !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.job;
  }

  public async getCustomerPostService(jobId: string): Promise<ActorPostService> {
    const id = jobIdSchema.parse(jobId);
    const response = await this.request(`/jobs/${id}/post-service`, {
      authenticated: true,
      schema: actorPostServiceResponseSchema,
    });
    if (response.postService.jobId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.postService;
  }

  public async getCustomerReview(jobId: string): Promise<ParticipantReview> {
    const id = jobIdSchema.parse(jobId);
    const response = await this.request(`/jobs/${id}/review`, {
      authenticated: true,
      schema: participantReviewResponseSchema,
    });
    if (response.review.reviewerSide !== "CUSTOMER") {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.review;
  }

  public async submitCustomerReview(
    jobId: string,
    input: ParticipantReviewInput,
    idempotencyKey: string,
  ): Promise<SubmitParticipantReviewResponse> {
    const id = jobIdSchema.parse(jobId);
    const body = participantReviewInputSchema.parse(input);
    const key = postServiceIdempotencyKeySchema.parse(idempotencyKey);
    const response = await this.request(`/jobs/${id}/review`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": key },
      body,
      schema: submitParticipantReviewResponseSchema,
    });
    if (
      response.review.reviewerSide !== "CUSTOMER" ||
      response.review.rating !== body.rating ||
      response.review.comment !== body.comment
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async disputeJobCompletion(
    jobId: string,
    input: DisputeJobCompletionInput,
    idempotencyKey: string,
  ): Promise<DisputeJobCompletionResponse> {
    const id = jobIdSchema.parse(jobId);
    const body = disputeJobCompletionInputSchema.parse(input);
    const key = postServiceIdempotencyKeySchema.parse(idempotencyKey);
    const response = await this.request(`/jobs/${id}/actions/dispute-completion`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": key },
      body,
      schema: disputeJobCompletionResponseSchema,
    });
    if (
      response.postService.jobId !== id ||
      response.postService.jobVersionNo !== body.jobVersionNo ||
      response.postService.outcome !== "DISPUTED" ||
      response.dispute.reasonCode !== body.reasonCode ||
      response.dispute.description !== body.description
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async getProviderNoShowEligibility(jobId: string): Promise<ProviderNoShowEligibility> {
    const id = jobIdSchema.parse(jobId);
    const response = await this.request(`/jobs/${id}/provider-no-show-eligibility`, {
      authenticated: true,
      schema: providerNoShowEligibilityResponseSchema,
    });
    if (response.eligibility.jobId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.eligibility;
  }

  public async reportProviderNoShow(
    jobId: string,
    input: ReportProviderNoShowInput,
    idempotencyKey: string,
  ): Promise<ReportProviderNoShowResponse> {
    const id = jobIdSchema.parse(jobId);
    const body = reportProviderNoShowInputSchema.parse(input);
    const key = postServiceIdempotencyKeySchema.parse(idempotencyKey);
    const response = await this.request(`/jobs/${id}/actions/report-provider-no-show`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": key },
      body,
      schema: reportProviderNoShowResponseSchema,
    });
    if (response.report.jobId !== id || response.report.jobVersionNo !== body.jobVersionNo + 1) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async getJobCompletionAcknowledgement(
    jobId: string,
  ): Promise<JobCompletionAcknowledgement> {
    const id = jobIdSchema.parse(jobId);
    const response = await this.request(`/jobs/${id}/completion-acknowledgement`, {
      authenticated: true,
      schema: jobCompletionAcknowledgementResponseSchema,
    });
    if (response.acknowledgement.jobId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.acknowledgement;
  }

  public async acknowledgeJobCompletion(
    jobId: string,
    input: AcknowledgeJobCompletionInput,
    idempotencyKey: string,
  ): Promise<AcknowledgeJobCompletionResponse> {
    const id = jobIdSchema.parse(jobId);
    const body = acknowledgeJobCompletionInputSchema.parse(input);
    const key = completionAcknowledgementIdempotencyKeySchema.parse(idempotencyKey);
    const response = await this.request(`/jobs/${id}/actions/acknowledge-completion`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": key },
      body,
      schema: acknowledgeJobCompletionResponseSchema,
    });
    if (
      response.acknowledgement.jobId !== id ||
      response.acknowledgement.jobVersionNo !== body.jobVersionNo
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async createJobReplacement(
    jobId: string,
    input: CreateJobReplacementInput,
    idempotencyKey: string,
  ): Promise<CreateJobReplacementResponse> {
    const id = jobIdSchema.parse(jobId);
    const body = createJobReplacementInputSchema.parse(input);
    const key = jobReplacementIdempotencyKeySchema.parse(idempotencyKey);
    const response = await this.request(`/jobs/${id}/actions/create-replacement`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": key },
      body,
      schema: createJobReplacementResponseSchema,
    });
    if (response.replacement.sourceJobId !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response;
  }

  public async createJob(input: CreateJobInput, idempotencyKey: string): Promise<JobDetail> {
    const body = createJobInputSchema.parse(input);
    const response = await this.request("/jobs", {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": idempotencyKey },
      body,
      schema: createJobResponseSchema,
    });
    if (
      response.job.propertyId !== body.propertyId ||
      (response.status === "created" &&
        (response.job.jobStatus !== "DRAFT" || response.job.bookingMode !== body.bookingMode))
    ) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    if (response.status === "created") {
      assertExactReplacement(
        body.services.map(({ serviceId, quantity }) => `${serviceId}|${quantity}`),
        response.job.services.map(({ serviceId, quantity }) => `${serviceId}|${quantity}`),
      );
    }
    return response.job;
  }

  public async updateJob(jobId: string, input: UpdateJobInput): Promise<JobDetail> {
    const id = jobIdSchema.parse(jobId);
    const body = updateJobInputSchema.parse(input);
    const response = await this.request(`/jobs/${id}`, {
      method: "PATCH",
      authenticated: true,
      body,
      schema: updateJobResponseSchema,
    });
    if (response.job.id !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertReplacementVersion(response.status, body.versionNo, response.job.versionNo);
    return response.job;
  }

  public async replaceJobServices(
    jobId: string,
    input: ReplaceJobServicesInput,
  ): Promise<JobDetail> {
    const id = jobIdSchema.parse(jobId);
    const body = replaceJobServicesInputSchema.parse(input);
    const response = await this.request(`/jobs/${id}/services`, {
      method: "PUT",
      authenticated: true,
      body,
      schema: updateJobResponseSchema,
    });
    if (response.job.id !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertReplacementVersion(response.status, body.versionNo, response.job.versionNo);
    assertExactReplacement(
      body.services.map(({ serviceId, quantity }) => `${serviceId}|${quantity}`),
      response.job.services.map(({ serviceId, quantity }) => `${serviceId}|${quantity}`),
    );
    return response.job;
  }

  public async publishJob(
    jobId: string,
    input: PublishJobInput,
    idempotencyKey: string,
  ): Promise<JobDetail> {
    const id = jobIdSchema.parse(jobId);
    const body = publishJobInputSchema.parse(input);
    const response = await this.request(`/jobs/${id}/actions/publish`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": idempotencyKey },
      body,
      schema: publishJobResponseSchema,
    });
    if (response.job.id !== id || response.job.jobStatus !== "OPEN") {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertReplacementVersion(
      response.status === "published" ? "updated" : "existing",
      body.versionNo,
      response.job.versionNo,
    );
    return response.job;
  }

  public async cancelJob(
    jobId: string,
    input: CancelJobInput,
    idempotencyKey: string,
  ): Promise<CancelJobResponse> {
    const id = jobIdSchema.parse(jobId);
    const body = cancelJobInputSchema.parse(input);
    const response = await this.request(`/jobs/${id}/actions/cancel`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": idempotencyKey },
      body,
      schema: cancelJobResponseSchema,
    });
    if (response.cancellation.jobId !== id || response.cancellation.jobStatus !== "CANCELLED") {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    assertReplacementVersion(
      response.status === "cancelled" ? "updated" : "existing",
      body.jobVersionNo,
      response.cancellation.jobVersionNo,
    );
    return response;
  }

  public async listPublishedLinenPackages(): Promise<PublishedLinenPackage[]> {
    return (
      await this.request("/catalog/linen-packages", {
        authenticated: true,
        schema: linenPackageCatalogResponseSchema,
      })
    ).data;
  }

  public async listLinenSubscriptions(): Promise<LinenSubscriptionSummary[]> {
    return (
      await this.request("/linen/subscriptions", {
        authenticated: true,
        schema: linenSubscriptionListResponseSchema,
      })
    ).data;
  }

  public async getLinenSubscription(subscriptionId: string): Promise<LinenSubscriptionDetail> {
    const id = linenSubscriptionIdSchema.parse(subscriptionId);
    const response = await this.request(`/linen/subscriptions/${id}`, {
      authenticated: true,
      schema: linenSubscriptionResponseSchema,
    });
    if (response.subscription.id !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.subscription;
  }

  public async createLinenSubscription(
    input: CreateLinenSubscriptionInput,
    idempotencyKey: string,
  ): Promise<LinenSubscriptionDetail> {
    const body = createLinenSubscriptionInputSchema.parse(input);
    return (
      await this.request("/linen/subscriptions", {
        method: "POST",
        authenticated: true,
        headers: { "idempotency-key": idempotencyKey },
        body,
        schema: createLinenSubscriptionResponseSchema,
      })
    ).subscription;
  }

  public async createLinenConfiguration(
    subscriptionId: string,
    input: CreateLinenConfigurationInput,
    idempotencyKey: string,
  ): Promise<LinenSubscriptionDetail> {
    const id = linenSubscriptionIdSchema.parse(subscriptionId);
    const body = createLinenConfigurationInputSchema.parse(input);
    const response = await this.request(`/linen/subscriptions/${id}/configurations`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": idempotencyKey },
      body,
      schema: createLinenConfigurationResponseSchema,
    });
    if (response.subscription.id !== id || response.subscription.currentConfiguration === null) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.subscription;
  }

  public async activateLinenSubscription(
    subscriptionId: string,
    input: ActivateLinenSubscriptionInput,
    idempotencyKey: string,
  ): Promise<LinenSubscriptionDetail> {
    const id = linenSubscriptionIdSchema.parse(subscriptionId);
    const body = activateLinenSubscriptionInputSchema.parse(input);
    const response = await this.request(`/linen/subscriptions/${id}/actions/activate`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": idempotencyKey },
      body,
      schema: activateLinenSubscriptionResponseSchema,
    });
    if (response.subscription.id !== id || response.subscription.status !== "ACTIVE") {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.subscription;
  }

  public async cancelLinenSubscription(
    subscriptionId: string,
    input: CancelLinenSubscriptionInput,
    idempotencyKey: string,
  ): Promise<LinenSubscriptionDetail> {
    const id = linenSubscriptionIdSchema.parse(subscriptionId);
    const body = cancelLinenSubscriptionInputSchema.parse(input);
    const response = await this.request(`/linen/subscriptions/${id}/actions/cancel`, {
      method: "POST",
      authenticated: true,
      headers: { "idempotency-key": idempotencyKey },
      body,
      schema: cancelLinenSubscriptionResponseSchema,
    });
    if (response.subscription.id !== id || response.subscription.status !== "CANCELLED") {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.subscription;
  }

  public async listLinenServicePlans(): Promise<LinenServicePlan[]> {
    return (
      await this.request("/linen/service-plans", {
        authenticated: true,
        schema: linenServicePlanListResponseSchema,
      })
    ).data;
  }

  public async getLinenServicePlan(servicePlanId: string): Promise<LinenServicePlan> {
    const id = linenServicePlanIdSchema.parse(servicePlanId);
    const response = await this.request(`/linen/service-plans/${id}`, {
      authenticated: true,
      schema: linenServicePlanResponseSchema,
    });
    if (response.servicePlan.id !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.servicePlan;
  }

  public async createLinenServicePlan(
    input: CreateLinenServicePlanInput,
    idempotencyKey: string,
  ): Promise<LinenServicePlan> {
    const body = createLinenServicePlanInputSchema.parse(input);
    return (
      await this.request("/linen/service-plans", {
        method: "POST",
        authenticated: true,
        headers: { "idempotency-key": idempotencyKey },
        body,
        schema: createLinenServicePlanResponseSchema,
      })
    ).servicePlan;
  }

  public async listLinenServicePlanCycles(servicePlanId: string): Promise<LinenCycle[]> {
    const id = linenServicePlanIdSchema.parse(servicePlanId);
    return (
      await this.request(`/linen/service-plans/${id}/cycles`, {
        authenticated: true,
        schema: linenCycleListResponseSchema,
      })
    ).data;
  }

  public async getLinenCycle(cycleId: string): Promise<LinenCycle> {
    const id = linenCycleIdSchema.parse(cycleId);
    const response = await this.request(`/linen/cycles/${id}`, {
      authenticated: true,
      schema: linenCycleResponseSchema,
    });
    if (response.cycle.id !== id) {
      throw new ApiError(200, "INVALID_API_RESPONSE", "Washbox returned an unexpected response");
    }
    return response.cycle;
  }

  public async listPropertyAddressCatalogStates(): Promise<PropertyAddressCatalogState[]> {
    return (
      await this.request("/property-address-catalog/states", {
        authenticated: true,
        schema: propertyAddressCatalogStatesResponseSchema,
      })
    ).data;
  }

  public async listPropertyAddressCatalogCounties(
    stateCode: string,
  ): Promise<PropertyAddressCatalogCounty[]> {
    return (
      await this.request(
        `/property-address-catalog/states/${encodeURIComponent(stateCode)}/counties`,
        { authenticated: true, schema: propertyAddressCatalogCountiesResponseSchema },
      )
    ).data;
  }

  public async listPropertyAddressCatalogCities(
    countyCode: string,
  ): Promise<PropertyAddressCatalogCity[]> {
    return (
      await this.request(
        `/property-address-catalog/counties/${encodeURIComponent(countyCode)}/cities`,
        { authenticated: true, schema: propertyAddressCatalogCitiesResponseSchema },
      )
    ).data;
  }

  public async listPropertyAddressCatalogPostalCodes(
    cityCode: string,
  ): Promise<PropertyAddressCatalogPostalCode[]> {
    return (
      await this.request(
        `/property-address-catalog/cities/${encodeURIComponent(cityCode)}/postal-codes`,
        { authenticated: true, schema: propertyAddressCatalogPostalCodesResponseSchema },
      )
    ).data;
  }

  public async listProperties(): Promise<PropertySummary[]> {
    return (
      await this.request("/properties", {
        authenticated: true,
        schema: propertyListResponseSchema,
      })
    ).data;
  }

  public async getProperty(propertyId: string): Promise<PropertyDetail> {
    const id = propertyIdSchema.parse(propertyId);
    return (
      await this.request(`/properties/${id}`, {
        authenticated: true,
        schema: propertyResponseSchema,
      })
    ).property;
  }

  public async createProperty(
    input: CreatePropertyInput,
    idempotencyKey: string,
  ): Promise<PropertyDetail> {
    const body = createPropertyInputSchema.parse(input);
    return (
      await this.request("/properties", {
        method: "POST",
        authenticated: true,
        headers: { "idempotency-key": idempotencyKey },
        body,
        schema: createPropertyResponseSchema,
      })
    ).property;
  }

  public async updateProperty(
    propertyId: string,
    input: UpdatePropertyInput,
  ): Promise<PropertyDetail> {
    const id = propertyIdSchema.parse(propertyId);
    const body = updatePropertyInputSchema.parse(input);
    return (
      await this.request(`/properties/${id}`, {
        method: "PATCH",
        authenticated: true,
        body,
        schema: propertyResponseSchema,
      })
    ).property;
  }

  public async archiveProperty(propertyId: string, versionNo: number): Promise<PropertyDetail> {
    const id = propertyIdSchema.parse(propertyId);
    return (
      await this.request(`/properties/${id}/actions/archive`, {
        method: "POST",
        authenticated: true,
        body: { versionNo },
        schema: archivePropertyResponseSchema,
      })
    ).property;
  }

  public async listPropertyAddresses(propertyId: string): Promise<PropertyAddress[]> {
    const id = propertyIdSchema.parse(propertyId);
    return (
      await this.request(`/properties/${id}/addresses`, {
        authenticated: true,
        schema: propertyAddressListResponseSchema,
      })
    ).data;
  }

  public async replacePropertyAddress(
    propertyId: string,
    input: ReplacePropertyAddressInput,
  ): Promise<PropertyDetail> {
    const id = propertyIdSchema.parse(propertyId);
    const body = replacePropertyAddressInputSchema.parse(input);
    return (
      await this.request(`/properties/${id}/addresses`, {
        method: "POST",
        authenticated: true,
        body,
        schema: propertyResponseSchema,
      })
    ).property;
  }

  public async listPropertyBeds(propertyId: string): Promise<PropertyBed[]> {
    const id = propertyIdSchema.parse(propertyId);
    return (
      await this.request(`/properties/${id}/beds`, {
        authenticated: true,
        schema: propertyBedListResponseSchema,
      })
    ).data;
  }

  public async replacePropertyBeds(
    propertyId: string,
    input: ReplacePropertyBedsInput,
  ): Promise<PropertyDetail> {
    const id = propertyIdSchema.parse(propertyId);
    const body = replacePropertyBedsInputSchema.parse(input);
    return (
      await this.request(`/properties/${id}/beds`, {
        method: "PUT",
        authenticated: true,
        body,
        schema: propertyResponseSchema,
      })
    ).property;
  }

  public async listPreferredCleaners(propertyId: string): Promise<PreferredCleaner[]> {
    const id = propertyIdSchema.parse(propertyId);
    return (
      await this.request(`/properties/${id}/preferred-cleaners`, {
        authenticated: true,
        schema: preferredCleanerListResponseSchema,
      })
    ).cleaners;
  }

  public async replacePreferredCleaners(
    propertyId: string,
    input: ReplacePreferredCleanersInput,
  ): Promise<PreferredCleaner[]> {
    const id = propertyIdSchema.parse(propertyId);
    const body = replacePreferredCleanersInputSchema.parse(input);
    return (
      await this.request(`/properties/${id}/preferred-cleaners`, {
        method: "PUT",
        authenticated: true,
        body,
        schema: replacePreferredCleanersResponseSchema,
      })
    ).cleaners;
  }

  public async searchCleaners(query: string): Promise<CleanerDirectoryEntry[]> {
    return (
      await this.request(`/cleaners?query=${encodeURIComponent(query)}`, {
        authenticated: true,
        schema: cleanerSearchResponseSchema,
      })
    ).cleaners;
  }

  public async listMarketplaceCleaners(limit = 25): Promise<CleanerMarketplaceEntry[]> {
    return (
      await this.request(`/cleaners/marketplace?limit=${limit}`, {
        authenticated: true,
        schema: cleanerMarketplaceListResponseSchema,
      })
    ).cleaners;
  }

  public async lookupCleaner(
    email: string | null,
    phone: string | null,
  ): Promise<ExistingCleanerLookup> {
    const query = new URLSearchParams();
    if (email !== null) query.set("email", email);
    if (phone !== null) query.set("phone", phone);
    return (
      await this.request(`/cleaner-invites/lookup?${query.toString()}`, {
        authenticated: true,
        schema: cleanerPlatformInvitationLookupResponseSchema,
      })
    ).cleaner;
  }

  public async listCleanerPlatformInvitations(): Promise<CleanerPlatformInvitation[]> {
    return (
      await this.request(`/cleaner-invites`, {
        authenticated: true,
        schema: cleanerPlatformInvitationListResponseSchema,
      })
    ).invitations;
  }

  public async createCleanerPlatformInvitation(
    input: CreateCleanerPlatformInvitationInput,
  ): Promise<CleanerPlatformInvitation> {
    const body = createCleanerPlatformInvitationInputSchema.parse(input);
    return (
      await this.request(`/cleaner-invites`, {
        method: "POST",
        authenticated: true,
        body,
        schema: createCleanerPlatformInvitationResponseSchema,
      })
    ).invitation;
  }

  public async revokeCleanerPlatformInvitation(invitationId: string): Promise<void> {
    const id = cleanerPlatformInvitationIdSchema.parse(invitationId);
    await this.request(`/cleaner-invites/${id}`, {
      method: "DELETE",
      authenticated: true,
      body: {},
      schema: revokeCleanerPlatformInvitationResponseSchema,
    });
  }

  public async listPreferredCleanerSets(): Promise<PreferredCleanerSet[]> {
    return (
      await this.request(`/preferred-cleaner-sets`, {
        authenticated: true,
        schema: preferredCleanerSetListResponseSchema,
      })
    ).sets;
  }

  public async createPreferredCleanerSet(
    input: CreatePreferredCleanerSetInput,
  ): Promise<PreferredCleanerSet> {
    const body = createPreferredCleanerSetInputSchema.parse(input);
    return (
      await this.request(`/preferred-cleaner-sets`, {
        method: "POST",
        authenticated: true,
        body,
        schema: createPreferredCleanerSetResponseSchema,
      })
    ).set;
  }

  public async deletePreferredCleanerSet(setId: string): Promise<void> {
    const id = preferredCleanerSetIdSchema.parse(setId);
    await this.request(`/preferred-cleaner-sets/${id}`, {
      method: "DELETE",
      authenticated: true,
      body: {},
      schema: deletePreferredCleanerSetResponseSchema,
    });
  }

  public async listJobCleanerInvitations(jobId: string): Promise<CleanerInvitation[]> {
    return (
      await this.request(`/jobs/${jobId}/cleaner-invitations`, {
        authenticated: true,
        schema: cleanerInvitationListResponseSchema,
      })
    ).invitations;
  }

  public async dispatchCleanerInvitations(jobId: string): Promise<number> {
    return (
      await this.request(`/jobs/${jobId}/cleaner-invitations/dispatch`, {
        method: "POST",
        authenticated: true,
        schema: cleanerInvitationDispatchResponseSchema,
      })
    ).invitationCount;
  }

  public async advanceCleanerInvitations(jobId: string): Promise<boolean> {
    return (
      (
        await this.request(`/jobs/${jobId}/cleaner-invitations/advance`, {
          method: "POST",
          authenticated: true,
          schema: cleanerInvitationChangedResponseSchema,
        })
      ).status === "changed"
    );
  }

  public async expireCleanerInvitations(jobId: string): Promise<boolean> {
    return (
      (
        await this.request(`/jobs/${jobId}/cleaner-invitations/expire`, {
          method: "POST",
          authenticated: true,
          schema: cleanerInvitationChangedResponseSchema,
        })
      ).status === "changed"
    );
  }

  public async listMyCleanerInvitations(
    status?: CleanerInvitationStatus,
  ): Promise<CleanerInvitationInboxEntry[]> {
    const query = status === undefined ? "" : `?status=${encodeURIComponent(status)}`;
    return (
      await this.request(`/cleaner-invitations${query}`, {
        authenticated: true,
        schema: cleanerInvitationInboxResponseSchema,
      })
    ).invitations;
  }

  public async acceptCleanerInvitation(invitationId: string): Promise<string | null> {
    return (
      await this.request(`/cleaner-invitations/${invitationId}/actions/accept`, {
        method: "POST",
        authenticated: true,
        schema: cleanerInvitationChangedResponseSchema,
      })
    ).jobId;
  }

  public async declineCleanerInvitation(invitationId: string): Promise<string | null> {
    return (
      await this.request(`/cleaner-invitations/${invitationId}/actions/decline`, {
        method: "POST",
        authenticated: true,
        schema: cleanerInvitationChangedResponseSchema,
      })
    ).jobId;
  }

  public async cancelCleanerInvitation(
    invitationId: string,
    reason?: string,
  ): Promise<string | null> {
    return (
      await this.request(`/cleaner-invitations/${invitationId}/actions/cancel`, {
        method: "POST",
        authenticated: true,
        body: reason === undefined ? {} : { reason },
        schema: cleanerInvitationChangedResponseSchema,
      })
    ).jobId;
  }

  public async getPropertyCalendar(propertyId: string): Promise<PropertyCalendar | null> {
    return (
      await this.request(`/properties/${propertyId}/calendar`, {
        authenticated: true,
        schema: propertyCalendarGetResponseSchema,
      })
    ).calendar;
  }

  public async connectPropertyCalendar(
    propertyId: string,
    calendarUrl: string,
  ): Promise<PropertyCalendar> {
    return (
      await this.request(`/properties/${propertyId}/calendar`, {
        method: "PUT",
        authenticated: true,
        body: { calendarUrl },
        schema: propertyCalendarUpsertResponseSchema,
      })
    ).calendar;
  }

  public async disconnectPropertyCalendar(propertyId: string): Promise<boolean> {
    return (
      (
        await this.request(`/properties/${propertyId}/calendar`, {
          method: "DELETE",
          authenticated: true,
          schema: propertyCalendarRevokeResponseSchema,
        })
      ).status === "revoked"
    );
  }

  public async listCheckoutRuns(propertyId: string): Promise<CheckoutRun[]> {
    return (
      await this.request(`/properties/${propertyId}/checkout-runs`, {
        authenticated: true,
        schema: checkoutRunListResponseSchema,
      })
    ).checkoutRuns;
  }

  public async listCheckoutExceptions(propertyId: string): Promise<CheckoutException[]> {
    return (
      await this.request(`/properties/${propertyId}/checkout-exceptions`, {
        authenticated: true,
        schema: checkoutExceptionListResponseSchema,
      })
    ).exceptions;
  }

  public async resolveCheckoutException(
    exceptionId: string,
    resolutionNote: string | null,
  ): Promise<boolean> {
    return (
      (
        await this.request(`/checkout-exceptions/${exceptionId}/resolve`, {
          method: "POST",
          authenticated: true,
          body: { resolutionNote },
          schema: checkoutExceptionResolveResponseSchema,
        })
      ).status === "resolved"
    );
  }

  public async listMyLinenOrders(): Promise<LinenOrder[]> {
    return (
      await this.request("/linen-orders", {
        authenticated: true,
        schema: linenOrderListResponseSchema,
      })
    ).orders;
  }

  private async linenOrderAction(
    orderId: string,
    action: "accept" | "decline" | "confirm-pickup" | "confirm-delivery" | "cancel",
  ): Promise<boolean> {
    return (
      (
        await this.request(`/linen-orders/${orderId}/actions/${action}`, {
          method: "POST",
          authenticated: true,
          schema: linenOrderChangedResponseSchema,
        })
      ).status === "changed"
    );
  }

  public acceptLinenOrder(orderId: string): Promise<boolean> {
    return this.linenOrderAction(orderId, "accept");
  }

  public declineLinenOrder(orderId: string): Promise<boolean> {
    return this.linenOrderAction(orderId, "decline");
  }

  public confirmLinenPickup(orderId: string): Promise<boolean> {
    return this.linenOrderAction(orderId, "confirm-pickup");
  }

  public confirmLinenDelivery(orderId: string): Promise<boolean> {
    return this.linenOrderAction(orderId, "confirm-delivery");
  }

  public cancelLinenOrder(orderId: string): Promise<boolean> {
    return this.linenOrderAction(orderId, "cancel");
  }

  public async getNotificationPreferences(): Promise<NotificationPreferences> {
    return (
      await this.request("/notification-preferences", {
        authenticated: true,
        schema: notificationPreferencesGetResponseSchema,
      })
    ).preferences;
  }

  public async upsertNotificationPreferences(
    preferences: NotificationPreferences,
  ): Promise<boolean> {
    return (
      (
        await this.request("/notification-preferences", {
          method: "PUT",
          authenticated: true,
          body: preferences,
          schema: notificationPreferencesUpsertResponseSchema,
        })
      ).status === "changed"
    );
  }
}

export function describeApiError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "NETWORK_ERROR") {
      return "We could not reach Washbox. Check your connection and try again.";
    }
    if (error.status === 429) {
      return "Too many requests were made. Please wait a moment and try again.";
    }
    const validationIssue = error.validationIssues[0];
    return validationIssue === undefined ? error.message : validationIssueSummary(validationIssue);
  }
  return "Something unexpected happened. Please try again.";
}
